import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Search, ShoppingBag } from "lucide-react";
import WishlistButton from "../components/WishlistButton";

const fmt = (n) => `$${Number(n).toFixed(2)}`;

export default function Products({ category }) {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState("");
    const { add } = useCart();
    const { isB2B, isAdmin } = useAuth();

    useEffect(() => { api.get(`/products?category=${category}`).then(r => setProducts(r.data)); }, [category]);

    const filtered = products.filter(p => !q || p.name.toLowerCase().includes(q.toLowerCase()) || p.description.toLowerCase().includes(q.toLowerCase()));
    const isWholesale = isB2B || isAdmin;
    const isTint = category === "tint";

    return (
        <div data-testid={`products-${category}`} className="max-w-7xl mx-auto px-6 md:px-12 py-16">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
                <div>
                    <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">{isTint ? "Premium Films" : "Pro Tools"}</div>
                    <h1 className="font-display text-5xl md:text-6xl tracking-tighter">{isTint ? "Window Tint Films" : "Installation Tools"}</h1>
                </div>
                <div className="relative w-full md:w-80">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B645C]" />
                    <Input data-testid="product-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search products…" className="pl-9 bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold" />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map((p, i) => (
                    <div key={p.id} data-testid={`product-card-${i}`} className="group bg-charcoal-800 border border-[#E2D4C1] hover:border-gold/40 transition-all flex flex-col">
                        <div className="aspect-[4/3] overflow-hidden bg-[#E2D4C1] relative">
                            <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            <div className="absolute top-3 right-3"><WishlistButton productId={p.id} testId={`wish-prod-${i}`} /></div>
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-2">{p.subcategory || category}</div>
                            <div className="font-display text-xl mb-2">{p.name}</div>
                            <p className="text-[#6B645C] text-sm mb-4 flex-1 line-clamp-2">{p.description}</p>
                            <div className="flex items-end justify-between mb-4">
                                <div>
                                    <div className="text-gold text-2xl font-medium">{fmt(p.price)}</div>
                                    {isWholesale && p.wholesale_price && (
                                        <div className="text-[11px] tracking-[0.2em] uppercase text-gold-light mt-1" data-testid={`wholesale-price-${i}`}>
                                            Wholesale: {fmt(p.wholesale_price)}
                                        </div>
                                    )}
                                </div>
                                <span className="text-[10px] uppercase tracking-wider text-[#6B645C]">{p.stock} in stock</span>
                            </div>
                            <Button
                                data-testid={`add-to-cart-${i}`}
                                onClick={() => { add({ ...p, price: isWholesale && p.wholesale_price ? p.wholesale_price : p.price }); toast.success(`${p.name} added to cart`); }}
                                className="w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold"
                            >
                                <ShoppingBag size={14} className="mr-2" /> Add to Cart
                            </Button>
                        </div>
                    </div>
                ))}
                {filtered.length === 0 && <div className="col-span-full text-[#6B645C] text-center py-20">No products found.</div>}
            </div>
        </div>
    );
}
